import { format } from 'date-fns'
import { z } from 'zod'

function dateToIsoString(d: Date | string): string {
  if (typeof d === 'string') {
    return d
  }
  return format(d, 'yyyy-MM-dd')
}

function getOptionalDateString(
  d: Date | string | undefined
): string | undefined {
  if (d == null) return undefined
  return dateToIsoString(d)
}

const isoDateString = z.union([z.date(), z.string()]).transform(dateToIsoString)

const optionalIsoDateString = z
  .union([z.date(), z.string()])
  .optional()
  .transform(getOptionalDateString)

const getOptinString = (v: boolean | 0 | 1 | '0' | '1') => {
  if (typeof v === 'boolean') {
    return v ? '1' : '0'
  }

  if (v === '1' || v === 1) return '1'
  return '0'
}

/** Accept boolean or 0|1, output string "0" | "1" (API shape). */
const optInFlag = z
  .union([
    z.boolean(),
    z.literal(0),
    z.literal(1),
    z.literal('0'),
    z.literal('1'),
  ])
  .transform(getOptinString)

const optionalOptInFlag = optInFlag.optional()

const salaryNumber = z
  .union([z.string(), z.number()])
  .optional()
  .transform((v) => {
    if (v === undefined || v === null || v === '') return undefined
    if (typeof v === 'number') {
      return v
    }
    const s = v.replace(',', '.').trim()
    if (s === '') return undefined
    const n = parseFloat(s)

    return Number.isNaN(n) ? undefined : n
  })

// Transform values on the server to the format expected by the ERP API
export const CofidisApplicationFormPayloadSchema = z.object({
  iban: z.string(),
  bankAccountHolderName: z.string(),
  bankAccountOpeningDate: isoDateString.optional(),
  editionMode: z.string(),
  title: z.string(),
  firstName: z.string(),
  lastName1: z.string(),
  lastName2: z.string(),
  idType: z.string(),
  idNumber: z.string(),
  idCessationOfValidityDate: isoDateString,
  nationality: z.string(),
  birthDate: isoDateString,
  birthCountry: z.string(),
  birthTown: z.string().optional(),
  phoneNumber: z.string(),
  phoneNumber2: z.string().optional(),
  email: z.string().email(),
  // Optional so app builds without the field keep working; ERP treats empty as E
  sustainableSupport: z.enum(['E', 'P']).optional(),
  familySituation: z.string(),
  familyExpenses: salaryNumber,
  childSupportNumber: z.string(),
  wayType: z.string(),
  wayDescription: z.string(),
  wayNumber: z.string(),
  floor: z.string().optional(),
  letter: z.string().optional(),
  additionalAddress: z.string().optional(),
  zipCode: z.string(),
  town: z.string(),
  country: z.string(),
  homeType: z.string(),
  homeExpenses: salaryNumber,
  entranceDate: isoDateString,
  professionCode: z.string(),
  contractType: z.string().optional(),
  contractStartDate: optionalIsoDateString,
  contractEndDate: optionalIsoDateString,
  salary: salaryNumber,
  salaryMonthsNumber: z.string().optional(),
  companyName: z.string().optional(),
  companyId: z.string().optional(),
  companyWayType: z.string().optional(),
  companyWayDescription: z.string().optional(),
  companyWayNumber: z.string().optional(),
  companyZipCode: z.string().optional(),
  companyTown: z.string().optional(),
  companyPhone: z.string().optional(),
  vedaconAcceptance: optInFlag,
  internalOptin: optionalOptInFlag,
  partnerOptin: optionalOptInFlag,
})

export type CofidisApplicationFormPayload = z.infer<
  typeof CofidisApplicationFormPayloadSchema
>
